import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Collapse,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import KpiCard from "../commercial-dashboard/KpiCard";
import { KpiGrid, Section } from "../commercial-dashboard/DashboardBits";
import {
  CHANNELS,
  MONTHS,
  MONTHS_SHORT,
  SEASONS,
  groupBy,
  keur,
  modelLabel,
  pct,
  pctSigned,
  realUnitCost,
  subformatLabel,
  unitMoney,
  unitStats,
  UnitStats,
  eur,
} from "./controlBoardModel";
import { Caret, ChannelSwatch, Note, Num, Signal } from "./ControlBoardBits";
import { TabProps } from "./boardData";
import {
  BuyType,
  ControlBoardChannel,
  ControlBoardRow,
} from "../../types/controlBoard";
import { useGetControlBoardQuery } from "../../store/apis/controlBoardApi";

/** Líneas con coste y unidades entregadas: las únicas que dan un coste unitario. */
export const withUnitCost = (rows: ControlBoardRow[]) =>
  rows.filter((r) => r.volume > 0 && r.realCost > 0);

const costTone = (diff: number) =>
  !Number.isFinite(diff)
    ? "none"
    : diff <= 0.005
      ? "good"
      : diff <= 0.05
        ? "warn"
        : "bad";

interface Selection {
  channel: ControlBoardChannel;
  objective: string;
}

/** Costes: coste unitario real de plataforma frente al previsto, su dispersión y su estacionalidad. */
const CostsTab: React.FC<TabProps> = ({ data }) => {
  const theme = useTheme();
  const [type, setType] = useState<BuyType>("CM");
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [selection, setSelection] = useState<Selection | null>(null);
  const detailRef = useRef<HTMLDivElement>(null);
  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const rows = useMemo(
    () => withUnitCost(data.rows).filter((r) => r.type === type),
    [data, type]
  );
  const groups = useMemo(
    () =>
      [...groupBy(rows, (r) => `${r.channel}|${r.objective}`).entries()]
        .map(([key, list]) => ({
          key,
          channel: list[0].channel,
          objective: list[0].objective,
          list,
          u: unitStats(list),
        }))
        .sort(
          (a, b) =>
            CHANNELS.indexOf(a.channel) - CHANNELS.indexOf(b.channel) ||
            b.u.sale - a.u.sale
        ),
    [rows]
  );

  const select = (channel: ControlBoardChannel, objective: string) => {
    setSelection({ channel, objective });
    setTimeout(
      () =>
        detailRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "start",
        }),
      0
    );
  };

  const isPM = type === "PM";
  const statRow = (
    label: React.ReactNode,
    u: UnitStats,
    indent: number,
    extra?: React.ReactNode,
    onClick?: () => void,
    bg?: string
  ) => {
    const diff = u.plannedUnitCost
      ? u.realUnitCost / u.plannedUnitCost - 1
      : NaN;
    return (
      <TableRow
        hover={!!onClick}
        onClick={onClick}
        sx={{ cursor: onClick ? "pointer" : "default", backgroundColor: bg }}
      >
        <TableCell sx={{ pl: indent }}>{label}</TableCell>
        <Num>{u.lines}</Num>
        <Num muted>{keur(u.sale)}</Num>
        <Num muted>{unitMoney(u.plannedUnitCost)}</Num>
        <Num sx={{ fontWeight: 600 }}>{unitMoney(u.realUnitCost)}</Num>
        <Num>
          {Number.isFinite(diff) ? (
            <Signal text={pctSigned(diff, 1)} tone={costTone(diff)} />
          ) : (
            "—"
          )}
        </Num>
        {!isPM && <Num muted>{pct(u.delivery, 0)}</Num>}
        <Num muted>
          {unitMoney(u.p10)} – {unitMoney(u.p90)}
        </Num>
        <TableCell>{extra}</TableCell>
      </TableRow>
    );
  };

  return (
    <Stack gap={2}>
      <Section
        title="Costes por formato"
        subtitle="Coste unitario que se paga a la plataforma frente al previsto en el briefing, y cómo varía de una campaña a otra. Pulsa un formato para ver sus subformatos; «Mes a mes» abre su detalle."
        action={
          <ToggleButtonGroup
            size="small"
            exclusive
            value={type}
            onChange={(_, v) => v && setType(v)}
          >
            <ToggleButton value="CM">Compra de medios</ToggleButton>
            <ToggleButton value="PM">Paid Media</ToggleButton>
          </ToggleButtonGroup>
        }
      >
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Formato</TableCell>
                <Num>Líneas</Num>
                <Num>Inversión</Num>
                <Num>Coste previsto</Num>
                <Num>Coste real</Num>
                <Num>Real vs previsto</Num>
                {!isPM && <Num>Entrega</Num>}
                <Num>Rango habitual</Num>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((g) => {
                const subs = [...groupBy(g.list, (r) => r.subformat).entries()]
                  .map(([s, l]) => ({ s, u: unitStats(l) }))
                  .sort((a, b) => b.u.sale - a.u.sale);
                return (
                  <React.Fragment key={g.key}>
                    {statRow(
                      <>
                        <Caret
                          open={open.has(g.key)}
                          onClick={() => toggle(g.key)}
                        />
                        <ChannelSwatch channel={g.channel} />
                        <strong>{g.channel}</strong>{" "}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                        >
                          {modelLabel(g.objective)} · {subs.length}
                        </Typography>
                      </>,
                      g.u,
                      1,
                      <Link
                        component="button"
                        underline="hover"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          select(g.channel, g.objective);
                        }}
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        Mes a mes ›
                      </Link>,
                      () => toggle(g.key)
                    )}
                    {open.has(g.key) &&
                      subs.map((sb) => (
                        <React.Fragment key={`${g.key}-${sb.s}`}>
                          {statRow(
                            subformatLabel(sb.s),
                            sb.u,
                            6,
                            null,
                            undefined,
                            theme.palette.grey[50]
                          )}
                        </React.Fragment>
                      ))}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        {groups.length === 0 && (
          <Note>
            No hay líneas con coste y unidades entregadas en {data.year}.
          </Note>
        )}
        <Note>
          <strong>Coste real:</strong> para cada línea y mes, coste pagado a la
          plataforma ÷ unidades entregadas (por mil en CPM); después, media
          simple de las líneas: cada campaña cuenta igual.{" "}
          <strong>Coste previsto:</strong> media del coste unitario de
          plataforma del briefing. <strong>Rango habitual:</strong> del 10 % al
          90 % de las líneas, para no dejarse llevar por casos raros.
        </Note>
      </Section>

      <Seasonality data={data} type={type} onSelect={select} />

      <div ref={detailRef} />
      {selection && (
        <CostDetail
          rows={rows}
          selection={selection}
          onChange={setSelection}
          onClose={() => setSelection(null)}
          isPM={isPM}
        />
      )}
    </Stack>
  );
};

const Seasonality: React.FC<{
  data: TabProps["data"];
  type: BuyType;
  onSelect: (channel: ControlBoardChannel, objective: string) => void;
}> = ({ data, type, onSelect }) => {
  const theme = useTheme();
  const [year, setYear] = useState(data.year);
  const [channel, setChannel] = useState<ControlBoardChannel | "">("");
  const [open, setOpen] = useState<Set<string>>(new Set());
  const other = useGetControlBoardQuery(year, { skip: year === data.year });
  const otherRows = other.data?.data?.rows;
  const rows = useMemo(() => {
    const source = year === data.year ? data.rows : (otherRows ?? []);
    return withUnitCost(source).filter((r) => r.type === type);
  }, [year, data, otherRows, type]);
  const groups = [
    ...groupBy(rows, (r) => `${r.channel}|${r.objective}`).entries(),
  ]
    .map(([key, list]) => ({
      key,
      channel: list[0].channel,
      objective: list[0].objective,
      list,
      u: unitStats(list),
    }))
    .filter((g) => g.list.length >= 5 && (!channel || g.channel === channel))
    .sort(
      (a, b) =>
        CHANNELS.indexOf(a.channel) - CHANNELS.indexOf(b.channel) ||
        b.u.sale - a.u.sale
    );
  const channels = [...new Set(rows.map((r) => r.channel))].sort(
    (a, b) => CHANNELS.indexOf(a) - CHANNELS.indexOf(b)
  );

  const cell = (list: ControlBoardRow[], mean: number, m: number) => {
    if (!list.length)
      return (
        <TableCell key={m} align="center" sx={{ color: "text.disabled" }}>
          ·
        </TableCell>
      );
    const u = unitStats(list);
    const d = u.realUnitCost / mean - 1;
    const few = list.length < 3;
    const k = Math.min(1, Math.abs(d) / 0.5);
    const bg = few
      ? theme.palette.grey[100]
      : d >= 0
        ? `rgba(239, 68, 68, ${0.08 + 0.45 * k})`
        : `rgba(16, 185, 129, ${0.08 + 0.45 * k})`;
    return (
      <TableCell
        key={m}
        align="center"
        title={`${MONTHS[m]} · ${list.length} líneas · ${unitMoney(u.realUnitCost)}`}
        sx={{
          backgroundColor: bg,
          color: few ? "text.disabled" : undefined,
          fontWeight: 600,
          whiteSpace: "nowrap",
          px: 0.75,
        }}
      >
        {pctSigned(d)}
        <Typography variant="caption" display="block" sx={{ fontWeight: 400 }}>
          {unitMoney(u.realUnitCost)}
        </Typography>
      </TableCell>
    );
  };

  return (
    <Section
      title="Estacionalidad de costes"
      subtitle="Coste de plataforma por unidad entregada, mes a mes, frente a su propio coste medio del año. Verde: más barato que su media; rojo: más caro. Las celdas con menos de 3 líneas van en gris."
      action={
        <Stack direction="row" gap={1}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="cs-f">Formato</InputLabel>
            <Select
              labelId="cs-f"
              label="Formato"
              value={channel}
              onChange={(e) =>
                setChannel(e.target.value as ControlBoardChannel | "")
              }
            >
              <MenuItem value="">Todos</MenuItem>
              {channels.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={year}
            onChange={(_, v) => v && setYear(v)}
          >
            {[data.year - 1, data.year].map((y) => (
              <ToggleButton key={y} value={y}>
                {y}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      }
    >
      {other.isFetching && year !== data.year ? (
        <Note>Cargando {year}…</Note>
      ) : groups.length === 0 ? (
        <Note>No hay suficientes líneas con coste en {year}.</Note>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Formato</TableCell>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <TableCell key={m} align="center" sx={{ px: 0.75 }}>
                    {MONTHS_SHORT[m]}
                    {SEASONS[m] && (
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                        sx={{ fontSize: 10 }}
                      >
                        {SEASONS[m]}
                      </Typography>
                    )}
                  </TableCell>
                ))}
                <TableCell align="center">Coste medio {year}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((g) => {
                const byMonth = groupBy(g.list, (r) => r.month);
                const isOpen = !!channel || open.has(g.key);
                const subs = [...groupBy(g.list, (r) => r.subformat).entries()]
                  .map(([s, l]) => ({ s, l, u: unitStats(l) }))
                  .sort((a, b) => b.u.sale - a.u.sale);
                return (
                  <React.Fragment key={g.key}>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Caret
                          open={isOpen}
                          onClick={() =>
                            setOpen((prev) => {
                              const next = new Set(prev);
                              if (next.has(g.key)) next.delete(g.key);
                              else next.add(g.key);
                              return next;
                            })
                          }
                        />
                        <ChannelSwatch channel={g.channel} />
                        <strong>{g.channel}</strong>{" "}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                        >
                          {modelLabel(g.objective)}
                        </Typography>
                      </TableCell>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((m) =>
                        cell(byMonth.get(m) ?? [], g.u.realUnitCost, m)
                      )}
                      <TableCell align="center">
                        <Link
                          component="button"
                          underline="hover"
                          sx={{ fontWeight: 600 }}
                          onClick={() => onSelect(g.channel, g.objective)}
                        >
                          {unitMoney(g.u.realUnitCost)}
                        </Link>
                      </TableCell>
                    </TableRow>
                    {isOpen &&
                      subs.map((sb) => {
                        const bm = groupBy(sb.l, (r) => r.month);
                        return (
                          <TableRow
                            key={`${g.key}-${sb.s}`}
                            sx={{ backgroundColor: theme.palette.grey[50] }}
                          >
                            <TableCell sx={{ pl: 6 }}>
                              {subformatLabel(sb.s)}{" "}
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                              >
                                {sb.l.length}
                              </Typography>
                            </TableCell>
                            {Array.from({ length: 12 }, (_, i) => i + 1).map(
                              (m) => cell(bm.get(m) ?? [], sb.u.realUnitCost, m)
                            )}
                            <TableCell align="center">
                              {unitMoney(sb.u.realUnitCost)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Section>
  );
};

const CostDetail: React.FC<{
  rows: ControlBoardRow[];
  selection: Selection;
  onChange: (s: Selection) => void;
  onClose: () => void;
  isPM: boolean;
}> = ({ rows, selection, onChange, onClose, isPM }) => {
  const [subformat, setSubformat] = useState("");
  const [client, setClient] = useState("");
  const [showCampaigns, setShowCampaigns] = useState(false);
  const channels = CHANNELS.filter((c) => rows.some((r) => r.channel === c));
  const base = rows.filter((r) => r.channel === selection.channel);
  const objectives = [...new Set(base.map((r) => r.objective))];
  const scoped = base.filter((r) => r.objective === selection.objective);
  const subs = [...new Set(scoped.map((r) => r.subformat ?? ""))].sort();
  const clients = [...new Set(scoped.map((r) => r.client))].sort((a, b) =>
    a.localeCompare(b, "es")
  );
  const filtered = scoped.filter(
    (r) =>
      (!subformat || (r.subformat ?? "") === subformat) &&
      (!client || r.client === client)
  );
  const u = unitStats(filtered);
  const diff = u.plannedUnitCost ? u.realUnitCost / u.plannedUnitCost - 1 : NaN;
  const byMonth = groupBy(filtered, (r) => r.month);

  return (
    <Section
      title={`${selection.channel}${subformat ? ` · ${subformatLabel(subformat)}` : ""} · ${modelLabel(selection.objective)}`}
      subtitle={`${u.lines} líneas${client ? ` de ${client}` : ""} con unidades entregadas.`}
      action={
        <Link component="button" onClick={onClose} underline="hover">
          Cerrar
        </Link>
      }
    >
      <Stack direction="row" gap={1.5} flexWrap="wrap" mb={2}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="cd-c">Formato</InputLabel>
          <Select
            labelId="cd-c"
            label="Formato"
            value={selection.channel}
            onChange={(e) => {
              const next = e.target.value as ControlBoardChannel;
              const obj =
                rows.find((r) => r.channel === next)?.objective ??
                selection.objective;
              setSubformat("");
              onChange({ channel: next, objective: obj });
            }}
          >
            {channels.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel id="cd-s">Subformato</InputLabel>
          <Select
            labelId="cd-s"
            label="Subformato"
            value={subformat}
            onChange={(e) => setSubformat(e.target.value)}
          >
            <MenuItem value="">Todos</MenuItem>
            {subs.map((s) => (
              <MenuItem key={s} value={s}>
                {subformatLabel(s || null)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="cd-o">Modelo</InputLabel>
          <Select
            labelId="cd-o"
            label="Modelo"
            value={selection.objective}
            onChange={(e) =>
              onChange({ ...selection, objective: e.target.value })
            }
          >
            {objectives.map((o) => (
              <MenuItem key={o} value={o}>
                {modelLabel(o)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel id="cd-k">Cliente</InputLabel>
          <Select
            labelId="cd-k"
            label="Cliente"
            value={client}
            onChange={(e) => setClient(e.target.value)}
          >
            <MenuItem value="">Todos</MenuItem>
            {clients.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <KpiGrid columns={4}>
        <KpiCard
          label="Coste previsto"
          value={unitMoney(u.plannedUnitCost)}
          caption="El que se puso en el briefing"
        />
        <KpiCard
          label="Coste real"
          value={unitMoney(u.realUnitCost)}
          caption={`${Number.isFinite(diff) ? pctSigned(diff, 1) : "—"} frente al previsto`}
          tone={
            costTone(diff) === "bad"
              ? "error"
              : costTone(diff) === "warn"
                ? "warning"
                : undefined
          }
        />
        {!isPM && (
          <KpiCard
            label="Entrega"
            value={pct(u.delivery, 0)}
            caption="Unidades entregadas frente al objetivo"
          />
        )}
        <KpiCard
          label="Rango habitual"
          value={`${unitMoney(u.p10)} – ${unitMoney(u.p90)}`}
          caption={`Mediana ${unitMoney(u.p50)} · 8 de cada 10 líneas caen aquí`}
        />
      </KpiGrid>

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
        Coste de plataforma por mes
      </Typography>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Mes</TableCell>
              <TableCell>Temporada</TableCell>
              <Num>Líneas</Num>
              <Num>Inversión</Num>
              <Num>Coste previsto</Num>
              <Num>Coste real</Num>
              <Num>Real vs previsto</Num>
              <Num>vs. media del año</Num>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: 12 }, (_, i) => i + 1)
              .filter((m) => byMonth.has(m))
              .map((m) => {
                const list = byMonth.get(m) as ControlBoardRow[];
                const mu = unitStats(list);
                const d = mu.plannedUnitCost
                  ? mu.realUnitCost / mu.plannedUnitCost - 1
                  : NaN;
                const dm = mu.realUnitCost / u.realUnitCost - 1;
                const few = list.length < 3;
                return (
                  <TableRow
                    key={m}
                    sx={{ color: few ? "text.disabled" : undefined }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{MONTHS[m]}</TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {SEASONS[m] ?? ""}
                    </TableCell>
                    <Num>{mu.lines}</Num>
                    <Num muted>{keur(mu.sale)}</Num>
                    <Num muted>{unitMoney(mu.plannedUnitCost)}</Num>
                    <Num sx={{ fontWeight: 600 }}>
                      {unitMoney(mu.realUnitCost)}
                    </Num>
                    <Num>
                      {Number.isFinite(d) ? (
                        <Signal text={pctSigned(d, 1)} tone={costTone(d)} />
                      ) : (
                        "—"
                      )}
                    </Num>
                    <Num>
                      {few ? (
                        pctSigned(dm)
                      ) : (
                        <Signal
                          text={pctSigned(dm)}
                          tone={
                            Math.abs(dm) < 0.1
                              ? "none"
                              : dm > 0
                                ? "bad"
                                : "good"
                          }
                        />
                      )}
                    </Num>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </Box>

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
        Por subformato
      </Typography>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Subformato</TableCell>
              <Num>Líneas</Num>
              <Num>Inversión</Num>
              {!isPM && <Num>Precio cliente</Num>}
              <Num>Coste previsto</Num>
              <Num>Coste real</Num>
              <Num>Real vs previsto</Num>
              <Num>Rango habitual</Num>
              {!isPM && <Num>Margen unitario</Num>}
            </TableRow>
          </TableHead>
          <TableBody>
            {[
              ...groupBy(
                scoped.filter((r) => !client || r.client === client),
                (r) => r.subformat
              ).entries(),
            ]
              .map(([s, l]) => ({ s, su: unitStats(l) }))
              .sort((a, b) => b.su.sale - a.su.sale)
              .map(({ s, su }) => {
                const d = su.plannedUnitCost
                  ? su.realUnitCost / su.plannedUnitCost - 1
                  : NaN;
                return (
                  <TableRow key={s ?? "none"}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {subformatLabel(s)}
                    </TableCell>
                    <Num>{su.lines}</Num>
                    <Num muted>{keur(su.sale)}</Num>
                    {!isPM && <Num>{unitMoney(su.unitPrice)}</Num>}
                    <Num muted>{unitMoney(su.plannedUnitCost)}</Num>
                    <Num sx={{ fontWeight: 600 }}>
                      {unitMoney(su.realUnitCost)}
                    </Num>
                    <Num>
                      {Number.isFinite(d) ? (
                        <Signal text={pctSigned(d, 1)} tone={costTone(d)} />
                      ) : (
                        "—"
                      )}
                    </Num>
                    <Num muted>
                      {unitMoney(su.p10)} – {unitMoney(su.p90)}
                    </Num>
                    {!isPM && (
                      <Num>
                        {pct(
                          su.unitPrice
                            ? 1 - su.realUnitCost / su.unitPrice
                            : NaN,
                          1
                        )}
                      </Num>
                    )}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </Box>

      <Link
        component="button"
        underline="hover"
        sx={{ mt: 3, fontWeight: 600 }}
        onClick={() => setShowCampaigns((v) => !v)}
      >
        {showCampaigns
          ? "Ocultar campañas"
          : `Ver las ${filtered.length} líneas`}
      </Link>
      <Collapse in={showCampaigns} unmountOnExit>
        <TableContainer sx={{ maxHeight: "60vh", mt: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Mes</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Línea</TableCell>
                <TableCell>Subformato</TableCell>
                <TableCell>Técnico</TableCell>
                <Num>Inversión</Num>
                {!isPM && <Num>Precio cliente</Num>}
                <Num>Coste previsto</Num>
                <Num>Coste real</Num>
                <Num>Real vs previsto</Num>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...filtered]
                .sort((a, b) => a.month - b.month || b.sale - a.sale)
                .slice(0, 400)
                .map((r) => {
                  const cost = realUnitCost(r);
                  const d = r.netUnitCost ? cost / r.netUnitCost - 1 : NaN;
                  return (
                    <TableRow key={`${r.lineId}-${r.month}`} hover>
                      <TableCell>{MONTHS_SHORT[r.month]}</TableCell>
                      <TableCell>{r.client}</TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 300,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={r.lineName}
                      >
                        {r.lineName}
                      </TableCell>
                      <TableCell>{subformatLabel(r.subformat)}</TableCell>
                      <TableCell>
                        {r.technicians.join(", ") || "Sin asignar"}
                      </TableCell>
                      <Num muted>{eur(r.sale)}</Num>
                      {!isPM && <Num>{unitMoney(r.grossUnitCost)}</Num>}
                      <Num muted>{unitMoney(r.netUnitCost)}</Num>
                      <Num sx={{ fontWeight: 600 }}>{unitMoney(cost)}</Num>
                      <Num>
                        {Number.isFinite(d) ? (
                          <Signal text={pctSigned(d, 1)} tone={costTone(d)} />
                        ) : (
                          "—"
                        )}
                      </Num>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Section>
  );
};

export default CostsTab;
